<template>
  <div
    ref="contentRef"
    class="selectable-text"
    @touchend="handleSelectionEnd"
    @mouseup="handleSelectionEnd"
  >
    <template v-for="segment in segments" :key="segment.key">
      <button
        v-if="segment.breakdown"
        type="button"
        class="selectable-text__breakdown"
        @click.stop="emit('edit-breakdown', segment.breakdown)"
      >
        {{ segment.text }}
      </button>
      <span v-else>{{ segment.text }}</span>
    </template>

    <button
      v-if="actionState.visible"
      type="button"
      class="selectable-text__action"
      :style="actionStyle"
      @click.stop="openCreate"
    >
      拆解
    </button>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, reactive, ref } from 'vue'
import { showToast } from 'vant'

import {
  buildBreakdownSegments,
  isSelectionOverlapping,
  normalizeSelectionText,
  resolveTrimmedSelection,
} from '../utils/youtube'

const props = defineProps({
  text: {
    type: String,
    default: '',
  },
  breakdowns: {
    type: Array,
    default: () => [],
  },
  sourceType: {
    type: String,
    required: true,
  },
  keyPrefix: {
    type: String,
    default: 'selectable',
  },
  defaultBreakdownType: {
    type: String,
    default: 'body',
  },
  overlapMessage: {
    type: String,
    default: '该区间已存在拆解，不能重复新增',
  },
})

const emit = defineEmits([
  'create-breakdown',
  'edit-breakdown',
])

const contentRef = ref(null)
const actionState = reactive({
  visible: false,
  left: 0,
  top: 0,
  sourceText: '',
  sourceStartOffset: null,
  sourceEndOffset: null,
  breakdownType: 'body',
})
let selectionTimer = null

const normalizedText = computed(() => normalizeSelectionText(props.text).trim())
const segments = computed(() => buildBreakdownSegments(normalizedText.value, props.breakdowns, props.keyPrefix))
const actionStyle = computed(() => ({
  left: `${actionState.left}px`,
  top: `${actionState.top}px`,
}))

function hideAction() {
  actionState.visible = false
  actionState.left = 0
  actionState.top = 0
  actionState.sourceText = ''
  actionState.sourceStartOffset = null
  actionState.sourceEndOffset = null
  actionState.breakdownType = props.defaultBreakdownType
}

function clearNativeSelection() {
  const selection = window.getSelection?.()
  if (selection?.rangeCount) {
    selection.removeAllRanges()
  }
}

function detectBreakdownType(fullText, startOffset, endOffset) {
  if (props.sourceType !== 'subtitle_selection') {
    return props.defaultBreakdownType
  }
  if (!fullText) return 'body'
  if (startOffset === 0) return 'opening'
  if (endOffset === fullText.length) return 'ending'
  return 'body'
}

function updateSelectionAction() {
  const contentElement = contentRef.value
  const selection = window.getSelection()
  if (!contentElement || !selection || selection.rangeCount <= 0 || selection.isCollapsed) {
    hideAction()
    return
  }

  const range = selection.getRangeAt(0)
  const commonAncestor = range.commonAncestorContainer
  const commonNode = commonAncestor.nodeType === Node.TEXT_NODE ? commonAncestor.parentNode : commonAncestor
  if (!commonNode || !contentElement.contains(commonNode)) {
    hideAction()
    clearNativeSelection()
    return
  }

  const rawSelectedText = normalizeSelectionText(selection.toString())
  if (!rawSelectedText.trim()) {
    hideAction()
    return
  }

  const contentRange = document.createRange()
  contentRange.selectNodeContents(contentElement)
  contentRange.setEnd(range.startContainer, range.startOffset)
  const rawStartOffset = normalizeSelectionText(contentRange.toString()).length
  const {
    sourceText,
    sourceStartOffset,
    sourceEndOffset,
  } = resolveTrimmedSelection(rawSelectedText, rawStartOffset)
  const fullText = normalizedText.value

  if (
    sourceStartOffset < 0
    || sourceEndOffset > fullText.length
    || sourceEndOffset <= sourceStartOffset
    || sourceText !== fullText.slice(sourceStartOffset, sourceEndOffset)
  ) {
    hideAction()
    clearNativeSelection()
    return
  }

  if (isSelectionOverlapping(props.breakdowns, sourceStartOffset, sourceEndOffset)) {
    hideAction()
    clearNativeSelection()
    showToast(props.overlapMessage)
    return
  }

  const rect = range.getBoundingClientRect()
  const left = Math.min(
    Math.max(12, rect.left + (rect.width / 2) - 28),
    Math.max(12, window.innerWidth - 72),
  )
  const top = rect.bottom + 12

  actionState.visible = true
  actionState.left = left
  actionState.top = Math.max(12, top)
  actionState.sourceText = sourceText
  actionState.sourceStartOffset = sourceStartOffset
  actionState.sourceEndOffset = sourceEndOffset
  actionState.breakdownType = detectBreakdownType(fullText, sourceStartOffset, sourceEndOffset)
  clearNativeSelection()
}

function scheduleSelectionAction() {
  if (selectionTimer !== null) {
    window.clearTimeout(selectionTimer)
  }
  selectionTimer = window.setTimeout(() => {
    selectionTimer = null
    updateSelectionAction()
  }, 80)
}

function handleSelectionEnd() {
  scheduleSelectionAction()
}

function openCreate() {
  if (!actionState.sourceText) return
  emit('create-breakdown', {
    sourceType: props.sourceType,
    sourceText: actionState.sourceText,
    sourceStartOffset: actionState.sourceStartOffset,
    sourceEndOffset: actionState.sourceEndOffset,
    breakdownType: actionState.breakdownType,
  })
  hideAction()
}

onBeforeUnmount(() => {
  if (selectionTimer !== null) {
    window.clearTimeout(selectionTimer)
  }
})
</script>

<style scoped>
.selectable-text {
  position: relative;
  padding-bottom: 50px;
  white-space: pre-wrap;
  word-break: break-word;
  user-select: text;
  -webkit-user-select: text;
  -webkit-touch-callout: none;
  -webkit-tap-highlight-color: transparent;
}

.selectable-text__breakdown {
  display: inline;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
  text-decoration-line: underline;
  text-decoration-style: dashed;
  text-decoration-color: rgba(225, 29, 72, 0.82);
  text-decoration-thickness: 1.5px;
  text-underline-offset: 4px;
}

.selectable-text__action {
  position: fixed;
  z-index: 50;
  height: 36px;
  padding: 0 14px;
  border: 0;
  border-radius: 999px;
  background: var(--yt-accent);
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  box-shadow: 0 12px 26px rgba(225, 29, 72, 0.28);
}
</style>
